import React, { useCallback, useEffect, useRef, useState } from "react";

import { Info, X } from "lucide-react";
import { useDispatch } from "react-redux";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import { SnipResultPopover } from "@/components/FilePreview/SnipResultPopover";
import { MapToReportBuilderDialog } from "@/components/MapToReportBuilderDialog";
import { stripBundlePrefix } from "@/utils/bundle-name";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { ReactComponent as DiamondsIcon } from "@assets/svg/diamonds.svg";
import { ReactComponent as EditIcon } from "@assets/svg/document-with-pen.svg";
import { ReactComponent as RegenIcon } from "@assets/svg/generate-blue.svg";

import {
  DeleteFileConfirmation,
  RegenerateFileConfirmation,
} from "@components/FilesListDataTable";
import { Button } from "@components/ui/button";
import { Textarea } from "@components/ui/textarea";

import { useExportCase } from "@hooks/mutations/use-export-case";
import {
  useRetrySummaryGeneration,
  useUpdateDocumentReviewStatus,
  useUpdateFileSummary,
} from "@hooks/mutations/use-files";

import { setMessage } from "@redux/notification";

type SummaryPanelProps = {
  file: DocSorterFileSchema | null;
  isVisible: boolean;
  onClose: () => void;
  onAccept?: () => void;
  onSaveNote?: (text: string) => void;
};

export const SummaryPanel: React.FC<SummaryPanelProps> = ({
  file,
  isVisible,
  onClose,
  onAccept,
  onSaveNote,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [summary, setSummary] = useState("");
  // Tracks whether the doctor has typed unsaved changes since the last
  // file-load / save. We resync `summary` from the server's
  // `file.full_summary` only when this is false — otherwise an external
  // update (regen-completes poll, sibling tab save) would clobber their
  // draft. Cleared on save success, cancel, and file switch.
  const [isDirty, setIsDirty] = useState(false);
  // Track the previously-rendered file id so we can distinguish
  // "different file selected" (always resync) from "same file, server
  // delivered a refreshed summary" (resync only when not dirty).
  const previousFileIdRef = useRef<string | null>(null);
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [mapText, setMapText] = useState<string | null>(null);
  const [isAcceptFlow, setIsAcceptFlow] = useState(false);

  const dispatch = useDispatch();
  const exportCase = useExportCase();
  const updateFileSummary = useUpdateFileSummary();
  const updateDocumentReviewStatus = useUpdateDocumentReviewStatus();
  const regenerateFile = useRetrySummaryGeneration();

  // Resync local `summary` from the server-provided `file.full_summary`.
  // Fires in two distinct cases:
  //   1. A different file was selected — always replace (and clear the
  //      dirty flag) so we don't carry edits across documents.
  //   2. The same file's summary changed (regen-completes poll, sibling
  //      tab save, etc.) — replace only when the doctor has not typed
  //      unsaved edits, so we never clobber their draft.
  // STP-92 fix: the previous version had a `!summary` guard that
  // permanently locked out case 2 once any text was present, which is
  // why "Regenerate" appeared to do nothing in the UI even though the
  // backend completed successfully.
  useEffect(() => {
    if (!file) return;
    const incoming = file.full_summary || "";

    if (file.id !== previousFileIdRef.current) {
      previousFileIdRef.current = file.id;
      setSummary(incoming);
      setIsDirty(false);
      return;
    }

    if (!isDirty && incoming !== summary) {
      setSummary(incoming);
    }
  }, [file, isDirty, summary]);

  const [isRegenConfirmOpen, setIsRegenConfirmOpen] = useState(false);

  // Fired from the ConfirmationModal's Confirm action — the modal owns
  // the user-facing yes/no decision so we never reach for the
  // browser-native window.confirm (jarring, unstyled, untestable).
  const handleConfirmRegenerate = () => {
    if (!file) return;
    regenerateFile.mutate(file.id, {
      onSuccess: () => {
        dispatch(
          setMessage({
            content: "Summary regeneration started successfully",
            type: "success",
          })
        );
        setIsRegenConfirmOpen(false);
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to regenerate summary";
        dispatch(
          setMessage({
            content: errorMessage,
            type: "error",
          })
        );
      },
    });
  };

  const handleSave = async () => {
    if (!file) return;

    updateFileSummary.mutate(
      {
        fileId: file.id,
        summary: { full_summary: summary },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          // Persisted — the draft is now the server's truth, so future
          // external updates may safely overwrite local state again.
          setIsDirty(false);
          dispatch(
            setMessage({
              content: "Full summary updated successfully",
              type: "success",
            })
          );
        },
        onError: (_error) => {
          dispatch(
            setMessage({
              content: "Failed to update full summary",
              type: "error",
            })
          );
        },
      }
    );
  };

  const handleSummaryMouseUp = useCallback(() => {
    if (isEditing) return;
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (text) {
      setSelectedText(text);
    }
  }, [isEditing]);

  const handleSelectionClose = useCallback(() => {
    setSelectedText(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const handleMapToReportBuilder = useCallback(() => {
    if (!selectedText) return;
    setMapText(selectedText);
    setSelectedText(null);
    window.getSelection()?.removeAllRanges();
  }, [selectedText]);

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Discard any unsaved keystrokes — restore the server's current
    // summary so the resync effect can update from external changes
    // again without first being explicitly dirtied.
    if (file) {
      setSummary(file.full_summary || "");
    }
    setIsDirty(false);
  };

  const handleStartAcceptFlow = () => {
    if (!file || !file.case?.id) {
      dispatch(
        setMessage({
          content: "No case information available",
          type: "error",
        })
      );
      return;
    }
    if (!summary) return;
    setIsAcceptFlow(true);
    setMapText(summary);
  };

  const handleAcceptMapped = async () => {
    if (!file || !file.case?.id) return;

    try {
      await updateDocumentReviewStatus.mutateAsync({
        fileId: file.id,
        reviewStatus: { is_document_reviewed: true },
      });

      dispatch(
        setMessage({
          content: "File accepted and marked as reviewed",
          type: "success",
        })
      );

      onClose();
    } catch (_error) {
      dispatch(
        setMessage({
          content: "Failed to accept file",
          type: "error",
        })
      );
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
      <div className="mb-4 flex flex-shrink-0 items-center justify-between">
        <div className="relative w-full">
          <h2 className="mb-2 flex items-center gap-1.5 text-lg font-semibold">
            <DiamondsIcon className="size-5" />
            AI Summary
          </h2>
          <div className="flex items-start gap-1.5 text-sm text-foreground-muted">
            <Info className="mt-0.5 size-4 flex-shrink-0" />
            <p>
              Always review and verify the AI-generated summary against the original
              source document before using it. Edit or replace the summary as needed.
              Only select Map to Report Builder once you are satisfied that the
              content is accurate and supported by the source material.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute right-[-10px] top-[-10px] h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-shrink-0 flex-row-reverse items-center space-x-2">
        <div className="flex items-center gap-2">
          {file && (
            <>
              {!isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  <EditIcon className="size-5" />
                  Edit
                </Button>
              )}

              <ConfirmationModal
                type="info"
                icon={<RegenIcon className="size-12 [&_path]:fill-primary" />}
                title="Regenerate Summary?"
                description={`Are you sure you want to regenerate the summary for "${stripBundlePrefix(file.name)}"? This will replace the current summary with a new one.`}
                confirmButtonText="Yes, Regenerate"
                cancelButtonText="Cancel"
                onConfirm={handleConfirmRegenerate}
                isLoading={regenerateFile.isPending}
                open={isRegenConfirmOpen}
                onOpenChange={setIsRegenConfirmOpen}
              >
                <Button variant="outline" size="sm">
                  <RegenIcon className="size-4 [&_path]:fill-primary" />
                  Regenerate
                </Button>
              </ConfirmationModal>
            </>
          )}
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-auto rounded-md border bg-gray-50 p-4"
        onMouseUp={handleSummaryMouseUp}
      >
        {!file ? (
          <div className="flex h-full items-center justify-center">
            <span className="text-muted-foreground text-sm">
              No document selected
            </span>
          </div>
        ) : file.status === "PROCESSING" ? (
          // The Azure summary function is generating a new summary right
          // now. Without this affordance the doctor sees the stale text
          // and assumes Regenerate did nothing — the originating
          // complaint for STP-92.
          <div className="flex h-full items-center justify-center">
            <span className="text-muted-foreground animate-pulse text-sm">
              Regenerating summary…
            </span>
          </div>
        ) : !summary && !isEditing ? (
          <div className="flex h-full items-center justify-center">
            <span className="text-muted-foreground text-sm">
              No summary available for this document.
            </span>
          </div>
        ) : isEditing ? (
          <Textarea
            placeholder="Write summary here"
            value={summary}
            onChange={(e) => {
              setSummary(e.target.value);
              setIsDirty(true);
            }}
            className="h-full w-full resize-none border-none bg-transparent p-0 text-sm leading-relaxed shadow-none [field-sizing:fixed] focus-visible:ring-0"
            disabled={updateFileSummary.isPending}
          />
        ) : (
          <div
            className="whitespace-pre-line break-words text-sm"
            onDoubleClick={() => setIsEditing(true)}
          >
            {summary}
          </div>
        )}
      </div>

      {selectedText && (
        <SnipResultPopover
          title="Selected Text"
          text={selectedText}
          isLoading={false}
          isProcessing={false}
          error={null}
          onClose={handleSelectionClose}
          onSaveNote={() => onSaveNote?.(selectedText)}
          onMapToReportBuilder={handleMapToReportBuilder}
        />
      )}

      <div className="my-4 flex flex-shrink-0 justify-end space-x-2">
        {!isEditing && (
          <Button
            size="sm"
            onClick={handleStartAcceptFlow}
            disabled={!file || !summary}
          >
            Map to Report Builder
          </Button>
        )}

        {isEditing && (
          <div className="mb-2 flex justify-end space-x-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={updateFileSummary.isPending}
              isLoading={updateFileSummary.isPending}
            >
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelEdit}
              disabled={updateFileSummary.isPending}
            >
              Cancel
            </Button>
          </div>
        )}

        <MapToReportBuilderDialog
          open={!!mapText}
          onOpenChange={(open) => {
            if (!open) {
              setMapText(null);
              setIsAcceptFlow(false);
            }
          }}
          text={mapText ?? ""}
          targetField="summary_from_doc_sorter"
          extractType="summary_highlight"
          caseNumber={file?.case?.case_number}
          templateId={file?.case?.template?.id}
          sourceFileName={file?.name}
          onMapped={isAcceptFlow ? handleAcceptMapped : undefined}
        />
      </div>
    </div>
  );
};
